import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Spinner,
} from '@saas-kit/ui';

import { fetchKioskLink } from './apiKiosk';

/**
 * Owner-facing self-service kiosk QR. Print it and post it in the waiting
 * hall — walk-ins scan it to issue their own queue tokens. Printing arms a
 * body class so the print stylesheet shows only the QR poster area.
 */
export function KioskQrCard(): JSX.Element {
  const { t } = useTranslation('qms');
  const query = useQuery({
    queryKey: ['qms-kiosk-link'],
    queryFn: fetchKioskLink,
    staleTime: Infinity,
  });

  const url = query.data
    ? `${window.location.origin}/qms/kiosk?t=${query.data.token}`
    : null;
  // The in-office lobby kiosk reuses the same link with `mode=office`, so
  // walk-ins with no phone get a token and print it on the centre's printer.
  const officeUrl = url ? `${url}&mode=office` : null;

  const printQr = (): void => {
    document.body.classList.add('qms-kiosk-print-armed');
    window.print();
    document.body.classList.remove('qms-kiosk-print-armed');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('kiosk.qr_title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {url ? (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="qms-kiosk-print-area flex flex-col items-center gap-3 rounded-xl bg-white p-4">
              <QRCodeSVG value={url} size={168} />
              <p className="text-xs text-neutral-600">
                {t('kiosk.qr_caption')}
              </p>
            </div>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {t('kiosk.qr_help')}
              </p>
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="block break-all text-xs text-primary underline"
              >
                {url}
              </a>
              <Button variant="secondary" onClick={printQr}>
                {t('kiosk.qr_print')}
              </Button>
            </div>
          </div>
        ) : (
          <Spinner />
        )}
        {officeUrl ? (
          <div className="space-y-2 border-t border-border pt-5">
            <p className="text-sm font-medium text-foreground">
              {t('kiosk.office_title')}
            </p>
            <p className="text-sm text-muted-foreground">
              {t('kiosk.office_help')}
            </p>
            <a
              href={officeUrl}
              target="_blank"
              rel="noreferrer"
              className="block break-all text-xs text-primary underline"
            >
              {officeUrl}
            </a>
            <Button
              variant="secondary"
              onClick={() => window.open(officeUrl, '_blank', 'noopener')}
            >
              {t('kiosk.office_open')}
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
